import type { RequestContext } from "../context/request-context.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { EquipmentService } from "../../equipment/equipment-service.js";
import type { TechnicalDocumentRepository } from "../../infrastructure/database/memory/in-memory-technical-document-repository.js";
import type { DocumentStorageService } from "./document-storage-service.js";
import { sha256Hex } from "./document-storage-service.js";
import { resolveInspectionBookContext } from "../../domain/inspection-book/book-context-resolver.js";
import type {
  TechnicalDocument,
  TechnicalDocumentFilter,
  TechnicalDocumentRevision,
  TechnicalDocumentType,
  TechnicalDocumentWithRevisions
} from "../../domain/documentation/technical-document.js";
import { currentRevisionOf } from "../../domain/documentation/technical-document.js";

const randomUUID = () => globalThis.crypto.randomUUID();

export interface UploadDocumentInput {
  equipmentId: string;
  documentType: TechnicalDocumentType;
  title: string;
  description?: string | null;
  fileContent: string; // dataURL/base64
  filename: string;
  mimeType: string;
  sizeBytes: number;
  revision?: string;
  documentDate?: string | null;
}

export interface AddRevisionInput {
  documentId: string;
  fileContent: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  revision?: string;
  documentDate?: string | null;
}

/**
 * DOCUMENTAÇÃO E BIBLIOTECA TÉCNICA (Bloco 03) — serviço oficial de
 * anexar/vincular/versionar/consultar/baixar documentos técnicos.
 *
 * O vínculo é SEMPRE resolvido a partir do Equipment real (clientId,
 * projectId, unitAreaId, contextType — o mesmo resolvedor do Book de
 * Inspeção). Nunca por nome de arquivo. Nova revisão nunca sobrescreve a
 * anterior (`repo.saveRevision` recusa reuso de id).
 */
export class TechnicalDocumentService {
  private readonly auth = new RbacAuthorizationService();

  constructor(
    private readonly repo: TechnicalDocumentRepository,
    private readonly storage: DocumentStorageService,
    private readonly equipmentService: EquipmentService,
    private readonly audit?: AuditRecorder
  ) {}

  /** ANEXAR DOCUMENTO — cria o documento com sua primeira revisão. */
  async upload(context: RequestContext, input: UploadDocumentInput): Promise<TechnicalDocumentWithRevisions> {
    this.auth.require(context, PermissionCode.DocumentWrite);

    const equipment = await this.equipmentService.getEquipment(context, input.equipmentId);
    if (!equipment) {
      throw new AppError(ErrorCode.Validation, "Equipment not found", { safeMessage: "Selecione um Equipamento/TAG válido — o vínculo é obrigatório e não pode ser digitado livremente." });
    }
    if (!input.title?.trim()) {
      throw new AppError(ErrorCode.Validation, "Title required", { safeMessage: "Informe o título/descrição do documento." });
    }
    if (!input.fileContent) {
      throw new AppError(ErrorCode.Validation, "File required", { safeMessage: "Anexe o arquivo." });
    }

    const bookContext = resolveInspectionBookContext(equipment);
    if (!bookContext.projectId) {
      throw new AppError(ErrorCode.Validation, "Project required", { safeMessage: "O Equipamento selecionado não está vinculado a um Projeto — vincule-o em Equipamentos & Ativos antes de anexar documentos." });
    }

    const now = new Date().toISOString();
    const documentId = `doc-${randomUUID()}`;
    const document: TechnicalDocument = {
      id: documentId,
      clientId: bookContext.clientId,
      projectId: bookContext.projectId,
      unitAreaId: bookContext.unitAreaId,
      contextType: bookContext.contextType,
      equipmentId: bookContext.equipmentId,
      equipmentTag: bookContext.equipmentTag,
      documentType: input.documentType,
      title: input.title.trim(),
      description: input.description?.trim() || null,
      status: "ACTIVE",
      createdAt: now,
      createdBy: context.userId || "system",
      updatedAt: now
    };
    await this.repo.save(document);

    const revision = await this.createRevision(context, document, input, now);
    await this.recordAudit(context, AuditAction.TechnicalDocumentUploaded, document, { revision: revision.revision, documentType: document.documentType, equipmentId: document.equipmentId });

    return { document, revisions: [revision] };
  }

  /** NOVA REVISÃO — nunca sobrescreve; o documento (vínculo/tipo) permanece o mesmo. */
  async addRevision(context: RequestContext, input: AddRevisionInput): Promise<TechnicalDocumentWithRevisions> {
    this.auth.require(context, PermissionCode.DocumentRevise);

    const document = await this.repo.findById(input.documentId);
    if (!document) throw new AppError(ErrorCode.NotFound, "Document not found", { safeMessage: "Documento não encontrado." });
    if (document.status === "INACTIVE") {
      throw new AppError(ErrorCode.Conflict, "Document inactive", { safeMessage: "Este documento está inativado — não é possível anexar nova revisão." });
    }

    const now = new Date().toISOString();
    const revision = await this.createRevision(context, document, input, now);
    const updated: TechnicalDocument = { ...document, updatedAt: now };
    await this.repo.save(updated);

    await this.recordAudit(context, AuditAction.TechnicalDocumentRevised, updated, { revision: revision.revision, previousRevisionCount: (await this.repo.listRevisions(document.id)).length - 1 });

    const revisions = await this.repo.listRevisions(document.id);
    return { document: updated, revisions };
  }

  private async createRevision(context: RequestContext, document: TechnicalDocument, input: { fileContent: string; filename: string; mimeType: string; sizeBytes: number; revision?: string; documentDate?: string | null }, now: string): Promise<TechnicalDocumentRevision> {
    const existingRevisions = await this.repo.listRevisions(document.id);
    const nextRevisionNumber = input.revision?.trim() || String(existingRevisions.length).padStart(2, "0");
    const storageRef = await this.storage.store(input.fileContent);
    const hash = await sha256Hex(input.fileContent);
    const revision: TechnicalDocumentRevision = {
      id: `rev-${randomUUID()}`,
      documentId: document.id,
      revision: nextRevisionNumber,
      storageRef,
      filename: input.filename,
      mimeType: input.mimeType,
      sizeBytes: input.sizeBytes,
      hash,
      documentDate: input.documentDate || null,
      uploadedAt: now,
      uploadedBy: context.userId || "system"
    };
    await this.repo.saveRevision(revision);
    return revision;
  }

  /** INATIVAÇÃO LÓGICA — o histórico de revisões nunca é apagado. */
  async inactivate(context: RequestContext, documentId: string, reason: string): Promise<TechnicalDocument> {
    this.auth.require(context, PermissionCode.DocumentCancel);
    if (!reason?.trim()) throw new AppError(ErrorCode.Validation, "Reason required", { safeMessage: "Informe o motivo da inativação." });

    const document = await this.repo.findById(documentId);
    if (!document) throw new AppError(ErrorCode.NotFound, "Document not found", { safeMessage: "Documento não encontrado." });

    const now = new Date().toISOString();
    const updated: TechnicalDocument = { ...document, status: "INACTIVE", inactivatedAt: now, inactivatedBy: context.userId || "system", inactivationReason: reason.trim(), updatedAt: now };
    await this.repo.save(updated);
    await this.recordAudit(context, AuditAction.TechnicalDocumentInactivated, updated, { reason: reason.trim() });
    return updated;
  }

  /** BIBLIOTECA — busca por vínculo real (equipmentId/projectId/...), nunca por nome de arquivo. */
  async search(context: RequestContext, filter: TechnicalDocumentFilter = {}): Promise<TechnicalDocumentWithRevisions[]> {
    this.auth.require(context, PermissionCode.DocumentRead);
    const all = await this.repo.listAll();
    const scoped = all.filter(doc => {
      if (context.activeClientId && doc.clientId !== context.activeClientId) return false;
      if (filter.clientId && doc.clientId !== filter.clientId) return false;
      if (filter.projectId && doc.projectId !== filter.projectId) return false;
      if (filter.unitAreaId && doc.unitAreaId !== filter.unitAreaId) return false;
      if (filter.equipmentId && doc.equipmentId !== filter.equipmentId) return false;
      if (filter.equipmentTag && !doc.equipmentTag.toLowerCase().includes(filter.equipmentTag.toLowerCase())) return false;
      if (filter.documentType && doc.documentType !== filter.documentType) return false;
      if (filter.status && doc.status !== filter.status) return false;
      return true;
    });

    const withRevisions = await Promise.all(scoped.map(async document => {
      const revisions = await this.repo.listRevisions(document.id);
      return { document, revisions };
    }));

    return withRevisions.filter(entry => {
      if (!filter.from && !filter.to) return true;
      const current = currentRevisionOf(entry);
      const date = current?.documentDate || current?.uploadedAt || "";
      if (filter.from && date < filter.from) return false;
      if (filter.to && date > filter.to) return false;
      return true;
    }).sort((a, b) => (currentRevisionOf(b)?.uploadedAt || "").localeCompare(currentRevisionOf(a)?.uploadedAt || ""));
  }

  async listRevisions(context: RequestContext, documentId: string): Promise<TechnicalDocumentRevision[]> {
    this.auth.require(context, PermissionCode.DocumentRead);
    return this.repo.listRevisions(documentId);
  }

  /** VISUALIZAR/BAIXAR — resolvido pelo StorageService (nunca expõe /data/files); registra download quando exigido. */
  async resolveDownload(context: RequestContext, revisionId: string, documentId: string): Promise<string | null> {
    this.auth.require(context, PermissionCode.DocumentRead);
    const revisions = await this.repo.listRevisions(documentId);
    const revision = revisions.find(r => r.id === revisionId);
    if (!revision) return null;
    const url = await this.storage.resolveDownloadUrl(revision.storageRef);
    if (url) {
      await this.recordAudit(context, AuditAction.TechnicalDocumentDownloaded, await this.repo.findById(documentId), { revisionId, revision: revision.revision });
    }
    return url;
  }

  private async recordAudit(context: RequestContext, action: AuditAction, document: TechnicalDocument | null, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit || !document) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: "TechnicalDocument",
        entityId: document.id,
        previousState: null,
        newState: { status: document.status, documentType: document.documentType } as any,
        metadata: { equipmentId: document.equipmentId, equipmentTag: document.equipmentTag, projectId: document.projectId, ...metadata } as any
      });
    } catch (e) {
      console.warn("[DOCUMENTAÇÃO] Falha ao registrar auditoria", e);
    }
  }
}
